import { isNil } from 'lodash-es'
import { appendQueryParams } from './url'

export type OriginalImageInfo = {
  aspectRatio?: number
}

export type ResizeParams =
  | {
      w?: number
      h?: number
      dpr?: number
    }
  | ({
      // 横向撑满，纵向裁剪，指定裁剪中心点
      w: number
      h: number
      dpr?: number
      /** 裁剪时，y 轴的锚点(0~1)，即以图片的 (fpY*100)% 处为中心进行裁剪 */
      fpY: number
    } & OriginalImageInfo)

export default function setImageUrlResizeParams(url: string, params: ResizeParams) {
  const parsedURL = new URL(url)

  if (UNSPLASH_HOSTS.includes(parsedURL.hostname) || PEXELS_HOSTS.includes(parsedURL.hostname)) {
    return setUnsplashOrPexelsResizeParams(parsedURL, params)
  }

  if (QINIU_HOSTS.includes(parsedURL.hostname)) {
    return setQiniuResizeParams(parsedURL, params)
  }

  return url
}

function setUnsplashOrPexelsResizeParams(url: URL, params: ResizeParams) {
  url.searchParams.delete('dpr')
  url.searchParams.delete('w')
  url.searchParams.delete('h')
  url.searchParams.delete('fit')
  url.searchParams.delete('crop')
  url.searchParams.delete('fp-y')

  if (!params.w && !params.h) return url.toString()

  url.searchParams.set('fit', 'crop')
  if (params.w) url.searchParams.set('w', (params.w * (params.dpr ?? DEFAULT_DPR)).toString())
  if (params.h) url.searchParams.set('h', (params.h * (params.dpr ?? DEFAULT_DPR)).toString())

  if ('fpY' in params && !isNil(params.fpY)) {
    url.searchParams.set('crop', 'focalpoint')
    url.searchParams.set('fp-y', `${params.fpY}`)
  }

  return url.toString()
}

function setQiniuResizeParams(url: URL, params: ResizeParams) {
  if (url.pathname.match(/(@[^@/]+)$/)) {
    const index = url.pathname.lastIndexOf('@')
    url.pathname = url.pathname.slice(0, index)
  }
  for (const key of url.searchParams.keys()) {
    if (key.startsWith('imageView2/') || key.startsWith('imageMogr2/')) {
      url.searchParams.delete(key)
    }
  }

  if (!params.w && !params.h) return url.toString()

  let resizeParams: string[]

  if ('fpY' in params && params.aspectRatio) {
    const w = params.w * (params.dpr ?? DEFAULT_DPR)
    const h = params.h * (params.dpr ?? DEFAULT_DPR)
    const originImageResizedHeight = w / params.aspectRatio
    const cropOffsetY = Math.round(originImageResizedHeight * params.fpY - h / 2)

    // https://developer.qiniu.com/dora/8256/tailoring
    resizeParams = ['imageMogr2', 'thumbnail', `${w}x`, 'crop', `!${w}x${h}a0a${cropOffsetY}`]
  } else {
    // https://developer.qiniu.com/dora/1279/basic-processing-images-imageview2
    resizeParams = ['imageView2', params.w && params.h ? '1' : '2']

    if (params.w) resizeParams.push(`w/${params.w * (params.dpr ?? DEFAULT_DPR)}`)
    if (params.h) resizeParams.push(`h/${params.h * (params.dpr ?? DEFAULT_DPR)}`)
  }

  const finalURL = url.toString()
  const resizeParamsString = resizeParams.join('/')

  return appendQueryParams(finalURL, resizeParamsString)
}

export function setQiniuImageUrlResizeSuffix(url: string, suffix: string) {
  const parsedURL = new URL(url)
  if (parsedURL.pathname.match(/(@[^@/]+)$/)) {
    const index = parsedURL.pathname.lastIndexOf('@')
    parsedURL.pathname = parsedURL.pathname.slice(0, index)
  }
  parsedURL.search = ''
  return parsedURL.toString() + `@${suffix}`
}

export function getImageUrlResizeParams(url: string, originalImageInfo?: OriginalImageInfo) {
  const parsedURL = new URL(url)

  if (UNSPLASH_HOSTS.includes(parsedURL.hostname) || PEXELS_HOSTS.includes(parsedURL.hostname)) {
    const w = +(parsedURL.searchParams.get('w') ?? '0')
    const h = +(parsedURL.searchParams.get('h') ?? '0')
    const dpr = +(parsedURL.searchParams.get('dpr') ?? '1') || 1
    const fpY = +(parsedURL.searchParams.get('fp-y') ?? '0.5')
    if (w && h) return { w: w * dpr, h: h * dpr, fpY: fpY }
    return
  }

  if (parsedURL.search.includes('imageView2')) {
    const matched = parsedURL.search.match(/imageView2\/\d\/w\/(\d+)\/h\/(\d+)/)
    if (matched) return { w: +matched[1]!, h: +matched[2]!, fpY: 0.5 }
  }

  if (parsedURL.search.includes('imageMogr2')) {
    const matched = parsedURL.search.match(/\/crop\/!?(\d+)x(\d+)a(\d+)a(\d+)/)
    if (!matched) return

    const w = +matched[1]!
    const h = +matched[2]!
    const offsetY = +matched[4]!

    const fpY = originalImageInfo?.aspectRatio ? (offsetY + h / 2) / (w / originalImageInfo.aspectRatio) : 0.5

    return { w, h, fpY }
  }
}

const DEFAULT_DPR = 2

const QINIU_HOSTS = [
  'jinshuju-testing-public.jinshuju.org',
  'jinshuju-testing-private.jinshuju.org',
  'gd-pub.jinshujufiles.com',
  'pub-gd.jinshujufiles.com',
  'gd-pri.jinshujufiles.com',
  'files.jinshuju.net',
]

const UNSPLASH_HOSTS = ['images.unsplash.com']
const PEXELS_HOSTS = ['images.pexels.com']

const GALLERY_IMAGE_HOSTS = [...UNSPLASH_HOSTS, ...PEXELS_HOSTS]
const EXTRA_GALLERY_IMAGE_URL_PREFIXES = [`${process.env.EXPO_PUBLIC_STATIC_ASSETS_DOMAIN}/next_ai/`]

export function isImageFromGallery(url: string) {
  return (
    GALLERY_IMAGE_HOSTS.some(host => url.includes(`://${host}`)) ||
    EXTRA_GALLERY_IMAGE_URL_PREFIXES.some(prefix => url.startsWith(prefix))
  )
}
